import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ROUTE_DEFINITIONS } from '@/routes';
import { useAppSelector } from '@/hooks';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useShiftChangeRequestsEmployeeExport } from '@/hooks/attendance/management/useShiftChangeRequestsEmployeeExport';
import { formatDateToYYYYMMDD } from '@/utils';
import { shiftService } from '@/api/domains/shift';
import { SHIFT_REQUEST_STATUS } from '@/api/domains/shift/shift.types';
import type { ShiftRequest, ShiftRequestStatus } from '@/api/domains/shift/shift.types';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { DateRangeValue } from '@/components/ui/filters/DateRangeFilter';


export interface ShiftRequestFilters {
  status?: ShiftRequestStatus | 'all';
  dateFrom?: string;
  dateTo?: string;
}

export interface UseShiftChangeRequestsResult {
  filters: ShiftRequestFilters;
  setFilters: React.Dispatch<React.SetStateAction<ShiftRequestFilters>>;
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  data: ShiftRequest[];
  totalRecords: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  openNewRequestPanel: () => void;
  onExport: () => Promise<void>;
  isExporting: boolean;
  columns: TableColumn<ShiftRequest>[];
  actions: TableAction<ShiftRequest>[];
}

// ASIS-5553: el rango se precarga con el último mes y se recalcula en cada entrada a la
// pantalla (ver `onHydrated`), de modo que la ventana relativa avance con "hoy" en vez de
// quedar congelada en la fecha de la última visita.
const buildDefaultDateRange = (): Pick<ShiftRequestFilters, 'dateFrom' | 'dateTo'> => {
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  return {
    dateFrom: formatDateToYYYYMMDD(oneMonthAgo),
    dateTo: formatDateToYYYYMMDD(today),
  };
};

// Mapeo de columnas de la tabla a los campos de ordenamiento del backend.
// El backend acepta: id | employeeName | requestDate | effectiveDate | status
const ORDER_BY_MAP: Record<string, string> = {
  id: 'id',
  requestDate: 'requestDate',
  dateFrom: 'effectiveDate',
  dateTo: 'effectiveDate',
  status: 'status',
};

export const useShiftChangeRequests = (): UseShiftChangeRequestsResult => {
  const { t: translate } = useTranslation();
  const { openSidePanel, openModal, closeModal, showNotification, showApiError } = useUI();
  const { canDelete } = usePagePermissions();

  const employeeId = useAppSelector((state) => state.auth.user?.idEmpleado ?? null);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<ShiftRequestFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<ShiftRequest> | null> => {
      if (!employeeId) return null;

      const response = await shiftService.getShiftRequests(
        {
          employeeId,
          status: params.filters.status === 'all' ? undefined : params.filters.status,
          dateFrom: params.filters.dateFrom,
          dateTo: params.filters.dateTo,
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
        },
        signal,
      );

      // Petición cancelada por un fetch más reciente (abort): ignorar en silencio.
      if (response.error?.code === 'ABORTED') return null;

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:shiftChange.messages.loadError'));
        return null;
      }

      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [employeeId, showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    debouncedSearchText,
    refetch,
  } = usePaginatedList<ShiftRequest, ShiftRequestFilters>({
    fetchFn,
    initialFilters: { status: 'all', ...buildDefaultDateRange() },
    initialSort: { key: 'requestDate', direction: 'DESC' },
    orderByMap: ORDER_BY_MAP,
    enabled: employeeId !== null,
    screenCode: ROUTE_DEFINITIONS.CAMBIO_TURNO_EMPLEADO.code,
    onHydrated: (hydrated) => ({ ...hydrated.filters, ...buildDefaultDateRange() }),
  });

  // ── Filtro Rango de fechas (ASIS-5553) ─────────────────────────────────────
  const dateRange: DateRangeValue = useMemo(
    () => ({ from: filters.dateFrom, to: filters.dateTo }),
    [filters.dateFrom, filters.dateTo],
  );

  const onDateRangeChange = useCallback(
    (range: DateRangeValue) => {
      setFilters((previous) => ({ ...previous, dateFrom: range.from, dateTo: range.to }));
    },
    [setFilters],
  );

  // ── Descarga Excel (ASIS-5555): consume los filtros vigentes ───────────────
  const { onExport, isExporting } = useShiftChangeRequestsEmployeeExport({
    employeeId,
    filters: useMemo(
      () => ({
        status: filters.status === 'all' ? undefined : filters.status,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        searchText: debouncedSearchText || undefined,
      }),
      [filters.status, filters.dateFrom, filters.dateTo, debouncedSearchText],
    ),
  });

  const openNewRequestPanel = useCallback(() => {
    openSidePanel({
      title: translate('attendance:shiftChange.form.newRequest'),
      component: 'ShiftRequestForm',
      props: {
        onSuccess: () => {
          refetch();
        },
      },
    });
  }, [openSidePanel, refetch, translate]);

  // ── Detalle de solo lectura (ASIS-5554) ────────────────────────────────────
  // Panel lateral, igual que la vista de gestión. Reutiliza la fila ya cargada por el
  // listado: no requiere una petición adicional.
  const handleViewDetail = useCallback(
    (row: ShiftRequest) => {
      openSidePanel({
        component: 'ShiftRequestDetailPanel',
        title: translate('attendance:shiftChange.detail.title'),
        props: { request: row },
      });
    },
    [openSidePanel, translate],
  );

  // ── Eliminar solicitud pendiente (ASIS-5556) ───────────────────────────────
  const handleDelete = useCallback(
    (requestId: number) => {
      if (!employeeId) return;

      openModal(
        'ConfirmModal',
        {
          variant: 'error',
          icon: 'alert',
          title: translate('attendance:shiftChange.messages.deleteTitle'),
          bodyText: translate('attendance:shiftChange.messages.deleteConfirm', { id: requestId }),
          confirmText: translate('common:actions.confirm'),
          cancelText: translate('common:actions.cancel'),
          onConfirm: async () => {
            const response = await shiftService.deleteShiftRequest(employeeId, requestId);

            closeModal('ConfirmModal');

            if (!response.success) {
              // Concurrencia: la solicitud pudo haber sido aprobada/rechazada mientras
              // el colaborador tenía la pantalla abierta. El backend responde 409 con el
              // detalle; se refresca el listado para dejarlo en el estado real.
              showApiError(response, translate('attendance:shiftChange.messages.deleteError'));
              refetch();
              return;
            }

            showNotification('success', translate('attendance:shiftChange.messages.deleteSuccess'), 4000);
            refetch();
          },
          onCancel: () => {
            closeModal('ConfirmModal');
          },
        },
      );
    },
    [employeeId, refetch, showApiError, showNotification, translate, openModal, closeModal],
  );

  const columns: TableColumn<ShiftRequest>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('attendance:shiftChange.table.id'),
        sortable: true,
      },
      {
        key: 'requestDate',
        header: translate('attendance:shiftChange.table.requestDate'),
        sortable: true,
      },
      {
        key: 'comments',
        header: translate('attendance:shiftChange.table.comments'),
        sortable: false,
        render: (row: ShiftRequest) => (
          <div className="max-w-xs truncate" title={row.comments}>
            {row.comments || '-'}
          </div>
        ),
      },
      {
        key: 'dateFrom',
        header: translate('attendance:shiftChange.table.dateFrom'),
        sortable: true,
      },
      {
        key: 'dateTo',
        header: translate('attendance:shiftChange.table.dateTo'),
        sortable: true,
      },
      {
        key: 'status',
        header: translate('attendance:shiftChange.table.status'),
        sortable: true,
      },
    ],
    [translate],
  );

  const actions: TableAction<ShiftRequest>[] = useMemo(
    () => [
      {
        id: 'detail',
        label: translate('common:tableActions.detail'),
        onClick: handleViewDetail,
        icon: 'eye',
      },
      {
        id: 'delete',
        label: translate('common:tableActions.delete'),
        onClick: (row: ShiftRequest) => handleDelete(row.id),
        icon: 'trash',
        variant: 'danger',
        hidden: (row: ShiftRequest) =>
          !canDelete || row.statusCode !== SHIFT_REQUEST_STATUS.PENDING,
      },
    ],
    [canDelete, handleDelete, handleViewDetail, translate],
  );

  return {
    // State
    filters,
    setFilters,
    dateRange,
    onDateRangeChange,
    data: rows,
    totalRecords: total,
    isLoading: loading,
    page,
    pageSize,

    // Actions
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    openNewRequestPanel,
    onExport,
    isExporting,

    // Table config
    columns,
    actions,
  };
};

export default useShiftChangeRequests;
